/**
 * Debug HUD for {@link BaseGameLoop}: stats.js FPS panel and Three.js addon inspector.
 */
import StatsJs from 'stats.js';
import * as THREE from 'three';
import { Inspector } from 'three/addons/inspector/Inspector.js';
import { Parameters } from 'three/addons/inspector/tabs/Parameters.js';
import { InspectorBase, setConsoleFunction } from 'three/webgpu';

import {
  applyThreeInspectorTogglePositioning,
  attachFloatingDraggableParametersGroup,
  type InspectorToggleOffset,
  type InspectorTogglePosition,
  nativeRendererHasInspectorSlot,
} from './debugInspectorOptions.js';

import type { Renderer } from '../render/Renderer.js';
import type { WebGPURenderer } from 'three/webgpu';

/** In-browser debug HUD driven by {@link BaseGameLoop}. */
export type DebugUIMode = 'none' | 'auto';

export interface GameLoopDebugUIOptions {
  renderer: Renderer;
  debugUIMode?: DebugUIMode;
  inspectorTogglePosition?: InspectorTogglePosition;
  inspectorToggleOffset?: InspectorToggleOffset;
  inspectorToggleScale?: number;
}

export class GameLoopDebugUI {
  private stats: StatsJs | null = null;
  private inspector: Inspector | null = null;
  private disposeProfilerPanel: (() => void) | null = null;

  static get DEFAULT_OPTIONS(): Required<
    Pick<GameLoopDebugUIOptions, 'debugUIMode' | 'inspectorTogglePosition' | 'inspectorToggleOffset' | 'inspectorToggleScale'>
    > {
    return {
      debugUIMode: 'auto',
      inspectorTogglePosition: 'top-left',
      inspectorToggleOffset: { x: 10, y: 10 },
      inspectorToggleScale: 0.8,
    };
  }

  constructor(
    private readonly container: HTMLElement,
    private readonly options: GameLoopDebugUIOptions,
  ) {}

  /** Positions the game host, stats.js panel, and Three.js inspector when applicable. */
  public async init(): Promise<void> {
    if (this.options.debugUIMode === 'none') {
      return;
    }

    const native = this.options.renderer.native;
    if (native.asWebGL()) {
      this.stats = new StatsJs();
      this.stats.dom.style.position = 'absolute';
      this.stats.dom.style.top = '10px';
      this.stats.dom.style.left = '10px';
      this.container.appendChild(this.stats.dom);
      this.stats.showPanel(0);
    } else {
      await this.setupInspector(native.asWebGPU()!);
    }

    this.setupProfiler();
  }

  public beginStats(): void {
    this.stats?.begin();
  }

  public endStats(): void {
    this.stats?.end();
  }

  public dispose(): void {
    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = null;
    this.disposeInspector();
    this.stats?.dom.remove();
    this.stats = null;
  }

  /** @returns New visibility state, or `null` when no debug HUD element exists. */
  public toggleStatsVisibility(): boolean | null {
    const el = this.stats?.dom ?? this.inspector?.domElement;
    if (!el) {
      return null;
    }
    const hidden = el.style.display === 'none' || el.style.visibility === 'hidden';
    const visible = hidden;
    el.style.display = visible ? '' : 'none';
    el.style.visibility = visible ? 'visible' : 'hidden';
    return visible;
  }

  private async setupInspector(webgpuRenderer: WebGPURenderer): Promise<void> {
    this.inspector = new Inspector();
    webgpuRenderer.inspector = this.inspector;
    const el = this.inspector.domElement;
    const defaults = GameLoopDebugUI.DEFAULT_OPTIONS;
    applyThreeInspectorTogglePositioning(el, {
      position: this.options.inspectorTogglePosition ?? defaults.inspectorTogglePosition,
      offset: {
        x: this.options.inspectorToggleOffset?.x ?? defaults.inspectorToggleOffset.x,
        y: this.options.inspectorToggleOffset?.y ?? defaults.inspectorToggleOffset.y,
      },
      scale: this.options.inspectorToggleScale ?? defaults.inspectorToggleScale,
    });

    this.container.appendChild(el);
  }

  private setupProfiler(): void {
    // THREE.ProfilerService.setProfile('full');
    const gui = new Parameters('').createGroup('Profiler');
    const actions = {
      toggle: () => {
        if (THREE.ProfilerService.isEnabled()) {
          THREE.ProfilerService.downloadTrace();
          THREE.ProfilerService.disable();
          toggleCtrl.name('▶ Start');
        } else {
          THREE.ProfilerService.setProfile('full');
          THREE.ProfilerService.enable();
          toggleCtrl.name('■ Stop + Download trace');
        }
      },
      report: () => THREE.ProfilerService.report(),
    };

    const toggleCtrl = gui.add(actions, 'toggle').name(THREE.ProfilerService.isEnabled() ? '■ Stop + Download trace' : '▶ Start');
    gui.add(actions, 'report').name('Report → console');

    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = attachFloatingDraggableParametersGroup(this.container, gui);
  }

  private disposeInspector(): void {
    const renderer = this.options.renderer;
    if (!this.inspector) {
      return;
    }
    const inspector = this.inspector as any;
    try {
      const genesysRenderer = renderer.native;
      const native = genesysRenderer.asWebGL() ?? genesysRenderer.asWebGPU();
      if (nativeRendererHasInspectorSlot(native)) {
        native.inspector = new InspectorBase();
      }

      const safeInspectorRenderer = {
        init: async () => { },
        _nodes: { nodeFrame: { deltaTime: 0, frameId: 0 } },
        resolveTimestampsAsync: async () => { },
        hasFeature: () => false,
        backend: {
          trackTimestamp: false,
          getTimestampFrames: () => [] as number[],
          hasTimestamp: () => false,
          getTimestamp: () => 0,
        },
      };

      const originalSetRenderer = inspector.setRenderer?.bind(inspector);
      const activeRenderer = inspector.getRenderer?.() as {
        resolveTimestampsAsync?: () => Promise<void>;
        backend?: { trackTimestamp?: boolean };
      } | null;
      if (activeRenderer) {
        activeRenderer.resolveTimestampsAsync = async () => { };
        if (activeRenderer.backend && 'trackTimestamp' in activeRenderer.backend) {
          activeRenderer.backend.trackTimestamp = false;
        }
      }
      setConsoleFunction((type: 'log' | 'warn' | 'error', message: string, ...params: unknown[]) => {
        if (type === 'error' && message.includes('THREE.Error resolving queries:')) {
          return;
        }
        if (type === 'warn') {
          console.warn(message, ...params);
          return;
        }
        if (type === 'error') {
          console.error(message, ...params);
          return;
        }
        console.log(message, ...params);
      });
      inspector.resolveTimestamp = async () => { };
      inspector._resolveTimestampPromise = null;
      inspector.addFrame = () => { };
      inspector._renderer = safeInspectorRenderer;
      if (originalSetRenderer) {
        inspector.setRenderer = (r: unknown) => {
          if (r === null) {
            return inspector;
          }
          if (
            typeof r === 'object'
            && r !== null
            && typeof (r as { init?: unknown }).init === 'function'
          ) {
            originalSetRenderer(r);
          }
          return inspector;
        };
      }
    } catch (error) {
      console.warn('[GameLoop] Failed to reset renderer inspector:', error);
    }
    this.inspector.domElement.remove();
    this.inspector = null;
  }
}
